import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  generateQueryString,
  generateUniqueId,
  getQueryParams,
  incrementStringNumber,
  isWindowAvailable,
  postMessageToParent,
} from "../utils";
import { convAiClient } from "../API";
import {
  BOT_ERROR_MSG,
  BOT_ROLE,
  IFRAME_ANIMATION_CLOSE_DELAY,
  initalChatInputConfigs,
  initialFooterModalConfig,
  initialModalConfig,
  initialOptionSelection,
  initialEditState,
} from "../constants/index.js";
import {
  IChatContext,
  IChatProviderProps,
  IFooterModalConfig,
  IModalConfig,
  Message,
  OptionSelectionConfig,
  ScheduleConfig,
  EditState,
} from "../types/ChatInterfaces";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { handleApiResponse } from "../utils/apiUtils";
import cloneDeep from "lodash.clonedeep";
import { removeMessageById } from "../utils/messageUtils";
import axios from "axios";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../utils/localStorage";
import { useLogoList } from "../hooks/useLogoList";
import { useTranslation } from "./TranslationContext";

const ChatContext = createContext<IChatContext>({
  messages: [],
  config: null,
  loading: true,
  error: null,
  activeView: "widget",
  botLoading: false,
  convId: "",
  modalConfig: initialModalConfig,
  optionSelection: initialOptionSelection,
  logoList: [],
  editState: initialEditState,
  showChat: () => {},
  hideChat: () => {},
  sendData: () => {},
  showWidgetButton: () => true,
  generatePayload: (data: any, allow_channel_shift?: string) => ({
    metadata: { convId: "1", turn_id: 0 },
    request: { ...data.action },
  }),
  genrateUserMsg: () => {},
  fetchBotResponse: async () => {},
  sendMessage: () => {},
  fetchConversation: () => {},
  createConversation: () => {},
  toggleBotInfo: () => {},
  handleAddMessage: () => {},
  setMessages: () => {},
  setModalConfig: () => {},
  setConvId: () => {},
  setOptionSelection: () => {},
  schedulerMsg: {
    style: { height: "38.8rem" },
    data: {},
    scheduleConfirmation: {},
  },
  setSchedulerMsg: () => {},
  getTurnId: () => "1",
  handleFormSubmit: () => {},
  reinitializeChat: () => {
    throw new Error("reinitializeChat function is not implemented");
  },
  handleUpdateMessages: () => {},
  handleInterviewSchedule: () => {},
  chatInputConfigs: initalChatInputConfigs,
  setChatInputConfigs: () => {},
  isPopupOpen: false,
  setIsPopupOpen: () => {},
  initializeData: {},
  setInitializeData: () => {},
  footerModalConfig: initialFooterModalConfig,
  setFooterModalConfig: () => {},
  cancelEditMessage: () => {},
  setEditState: () => {},
});

export const useChat = () => useContext(ChatContext);
export const ChatProvider: React.FC<IChatProviderProps> = ({
  config,
  updateConfig,
  children,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>("widget");
  const [botLoading, setBotLoading] = useState<boolean>(true);
  const [convId, setConvId] = useState<string>(
    getLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "convId",
    }) || ""
  );
  const [modalConfig, setModalConfig] =
    useState<IModalConfig>(initialModalConfig);
  const [optionSelection, setOptionSelection] = useState<OptionSelectionConfig>(
    initialOptionSelection
  );
  const [schedulerMsg, setSchedulerMsg] = useState<ScheduleConfig>({
    style: { height: "38.8rem" },
    data: {},
    scheduleConfirmation: {},
  });
  const [chatInputConfigs, setChatInputConfigs] = useState({
    disableInput: false,
    placeholder: config?.placeholder || [],
  });
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(
    !(
      isWindowAvailable &&
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "isPopupOpen",
      }) === "false"
    )
  );
  const [footerModalConfig, setFooterModalConfig] =
    useState<IFooterModalConfig>(initialFooterModalConfig);
  const [editState, setEditState] = useState<EditState>(initialEditState);
  const { logoList } = useLogoList(config?.agencyId);
  const [initializeData, setInitializeData] = useState<any>({});
  const theme = useTheme<Theme>();
  // @ts-ignore
  const onlyMobileScreen = useMediaQuery(theme.breakpoints.down("tablet"), {
    noSsr: true,
  });
  const abortControllerRef = useRef(new AbortController());
  const pendingMessagesRef = useRef<any | null>(null);
  const { t } = useTranslation();

  const showChat = () => {
    setActiveView("side");
    postMessageToParent("UI.SET-CLASS", [
      "joveo-widget-web",
      `joveo-widget-side`,
    ]);
    postMessageToParent("UI.OPENED", undefined);
  };

  const hideChat = () => {
    const widgetPopupOpen = !(
      isWindowAvailable &&
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "isPopupOpen",
      }) === "false"
    );

    setActiveView("widget");
    setModalConfig((prevState) => ({
      ...prevState,
      open: false,
      type: "",
    }));
    setTimeout(() => {
      postMessageToParent("UI.SET-CLASS", [
        "joveo-widget-web",
        `joveo-widget-${widgetPopupOpen ? "widget" : "popup-close"}`,
      ]);
      postMessageToParent("UI.CLOSED", undefined);
    }, IFRAME_ANIMATION_CLOSE_DELAY);
  };

  const handleMailLink = async (data: any) => {
    if (!data?.is_mail) return;
    handleClearOldData();
    abortControllerRef.current.abort();
    showChat();
    const { is_mail, ...restData } = data || {};
    const myQuery = generateQueryString(restData);
    const extractedConfig = config;
    await fetchInitialMsg(extractedConfig?.botId, true, myQuery);
  };

  const hideChatbotVisibility = () => {
    setActiveView("hidden");
    setTimeout(() => {
      postMessageToParent("UI.SET-CLASS", [
        "joveo-widget-web",
        `joveo-widget-hidden`,
      ]);
    }, IFRAME_ANIMATION_CLOSE_DELAY + 100);
  };

  const handeQueryParam = (data: any) => {
    const completeUrl = data?.value || "";

    const urlQuery = new URL(completeUrl);
    if (!urlQuery) return;
    const myQuery = getQueryParams(urlQuery?.search || "");

    if (myQuery?.joveoBotHide) {
      hideChatbotVisibility();
      return;
    }

    if (
      myQuery?.is_mail &&
      myQuery?.candidate_id &&
      myQuery?.job_id &&
      myQuery?.bot_skill
    ) {
      handleMailLink({
        is_mail: myQuery?.is_mail,
        candidate_id: myQuery?.candidate_id,
        job_id: myQuery?.job_id,
        bot_skill: myQuery?.bot_skill,
      });
      if (myQuery?.hideDetailPageApply) {
        updateConfig({ hideDetailPageApply: true });
      }
    }
  };

  const sendData = (data: any) => {
    if (data?.payload?.type === "queryParam") {
      handeQueryParam(data?.payload?.data);
    }
  };

  const showWidgetButton = () => {
    return !config?.hideWidget;
  };

  useEffect(() => {
    setChatInputConfigs((prev) => ({
      ...prev,
      placeholder: config?.placeholder || [],
    }));
  }, [config?.placeholder]);

  const getTurnId = (incrementCount?: number) => {
    if (messages?.length) {
      return (
        incrementStringNumber(
          messages[messages.length - 1]?.turn_id,
          incrementCount
        ) ?? "1"
      );
    }
    return "1";
  };

  const generatePayload = (data: any, allow_channel_shift?: string) => {
    return {
      metadata: {},
      request: {
        turn_id: getTurnId(),
        ...data.action,
      },
    };
  };

  const genrateUserMsg = (data: any) => {
    const val: Message = {
      role: "user",
      type: "text",
      timestamp: new Date().toISOString(),
      id: generateUniqueId(),
      turn_id: getTurnId(0),
      body: data?.body || {},
    };
    if (!data.body) return null;
    setMessages((previous) => [...previous, val]);
    return val;
  };
  const handleClearOldData = () => {
    removeLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "convId",
    });
    removeLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "preferred_language",
    });
    setConvId("");
    setMessages([]);
    setModalConfig(initialModalConfig);
    setOptionSelection(initialOptionSelection);
  };
  const fetchBotResponse = async (data: any, overrideState?: any) => {
    let conversationId = convId;
    const botId = config?.botId;
    if (
      !convId ||
      messages?.length === 0 ||
      botId !==
        getLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "bot_id",
        })
    ) {
      await reinitializeChat();
      conversationId =
        getLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "convId",
        }) || convId;
    }
    const newUserMsg = genrateUserMsg(data);
    setBotLoading(true);

    const payload = generatePayload(data);
    const isQueryApi = config?.isQueryApi ? `?show_demo_script=true` : "";
    const isPreview = config?.isPreview ? `/preview` : "";
    const isConversationId = conversationId ? `/${conversationId}` : "";
    try {
      const response = await convAiClient.post(
        `/v1/bots/${botId}${isPreview}/conversations${isConversationId}${isQueryApi}`,
        payload
      );

      if (response?.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const metadata = response?.data?.metadata;
      const dataResp = response?.data?.response;
      if (metadata?.conversation_id) {
        setConvId(metadata?.conversation_id);
        setLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "convId",
          value: metadata?.conversation_id || "",
        });
        setLocalStorageItem({
          botId: config?.botId,
          filterId: config?.filterId,
          key: "bot_id",
          value: metadata?.bot_id || "",
        });
      }

      if (dataResp?.channel === "web") {
        setChatInputConfigs((prev) => {
          const newConfigs = { ...prev, value: "" };

          if (dataResp?.hasOwnProperty("allow_free_text")) {
            newConfigs.disableInput = !dataResp.allow_free_text;
          }

          if (
            Array.isArray(dataResp?.option_phrases) &&
            dataResp?.option_phrases?.length > 0
          ) {
            newConfigs.placeholder = dataResp.option_phrases;
          }

          return newConfigs;
        });

        if (dataResp?.error?.restart_conversation) {
          setChatInputConfigs((prev) => ({ ...prev, disableInput: true }));
          setTimeout(() => {
            reinitializeChat();
          }, 2000);
        }

        if (dataResp?.messages?.length > 0) {
          const clonedMessage = overrideState?.messages
            ? overrideState?.messages
            : cloneDeep(messages);

          if (newUserMsg) clonedMessage.push(newUserMsg);
          const overriddenModalConfig = overrideState?.modalConfig
            ? overrideState?.modalConfig
            : modalConfig;
          const overriddenMessages = clonedMessage;
          const { _messages, _modalConfig } = handleApiResponse(
            dataResp.messages,
            onlyMobileScreen,
            {
              modalConfig: overriddenModalConfig,
              messages: overriddenMessages,
            }
          );
          setModalConfig((prevState) => ({
            ...prevState,
            ..._modalConfig,
          }));
          setMessages(_messages);
        } else throw new Error("no msg found");
      }
    } catch (error) {
      setMessages((previous) => [
        ...previous,
        {
          role: BOT_ROLE,
          turn_id: getTurnId(2),
          body: {
            text: t("pleaseTryAgain") || BOT_ERROR_MSG,
          },
          timestamp: new Date().toISOString(),
          type: "text",
          id: generateUniqueId(),
        },
      ]);
      setModalConfig(initialModalConfig);
    } finally {
      setBotLoading(false);
    }
  };

  const sendMessage = (message: string) => {
    // TODO:  the functionality to send the message
    console.log("Sending message:", message);
  };

  const fetchConversation = () => {
    // TODO:  the functionality to fetch the message
    reinitializeChat();
  };

  const createConversation = (message: string) => {
    // TODO:  the functionality to create the message
    console.log("Create message:", message);
  };

  const toggleBotInfo = (message: string) => {
    // TODO:  the functionality to toggleBotInfo the message
    console.log("toggleBotInfo message:", message);
  };

  const setLoadingCompleted = (extractedConfig: any) => {
    if (extractedConfig?.zIndex) {
      postMessageToParent("UI.FRAME_STYLE_UPDATE", {
        style: { zIndex: extractedConfig?.zIndex },
      });
    }
    postMessageToParent("LIFECYCLE.LOADED", undefined);
  };

  useEffect(() => {
    const extractedConfig = config;
    let isInitialize = false;
    if (
      extractedConfig?.botId !==
      getLocalStorageItem({
        botId: config?.botId,
        filterId: config?.filterId,
        key: "bot_id",
      })
    ) {
      isInitialize = true;
    }
    if (!extractedConfig?.botId) {
      hideChatbotVisibility();
      return;
    }

    fetchInitialMsg(extractedConfig?.botId, isInitialize);
    if (extractedConfig !== null) {
      setLoading(false);
      setLoadingCompleted(extractedConfig);
    } else {
      setError("Error while extracting config");
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Apply the pending messages if activeState changes to 'side'
    if (activeView === "side" && pendingMessagesRef.current) {
      setBotLoading(true);
      const delayTimer = setTimeout(() => {
        const dataRespMsg = pendingMessagesRef.current;

        const { _messages, _modalConfig } = handleApiResponse(
          dataRespMsg || [],
          onlyMobileScreen,
          {
            modalConfig: initialModalConfig,
            messages: [],
          },
          { sortMsg: false } //why: check Pr comment
        );
        setModalConfig((prevState) => ({
          ...prevState,
          ..._modalConfig,
          open: false,
        }));
        setMessages(_messages);
        setBotLoading(false);
        pendingMessagesRef.current = null;
      }, IFRAME_ANIMATION_CLOSE_DELAY + 100);

      return () => clearTimeout(delayTimer);
    }
  }, [activeView, pendingMessagesRef.current?.length, onlyMobileScreen]);

  const handleAddMessage = (newMessage: any) => {
    setMessages((previous) => [...previous, ...newMessage]);
  };

  const fetchInitialMsg = async (
    botId: string,
    reInitialize?: boolean,
    queryString?: string
  ) => {
    if (!botId) return;
    setBotLoading(true);
    try {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      if (!convId || reInitialize) {
        const isQueryApi = config?.isQueryApi ? `?show_demo_script=true` : "";
        const apiUrl = config?.isPreview
          ? `preview/conversations${isQueryApi}`
          : "conversations";
        const response = await convAiClient.get(
          `/v1/bots/${botId}/${apiUrl}${queryString ? "?" + queryString : ""}`,
          {
            signal: abortControllerRef.current.signal,
          }
        );

        if (response?.status !== 200) {
          throw new Error("Network response was not ok");
        }
        const metadata = response?.data?.metadata;
        const dataResp = response?.data?.response;
        if (metadata?.conversation_id) {
          setConvId(metadata?.conversation_id);
          setLocalStorageItem({
            botId: config?.botId,
            filterId: config?.filterId,
            key: "convId",
            value: metadata?.conversation_id || "",
          });
          setLocalStorageItem({
            botId: config?.botId,
            filterId: config?.filterId,
            key: "bot_id",
            value: metadata?.bot_id || "",
          });
        }

        if (dataResp?.channel === "web") {
          setChatInputConfigs((prev) => {
            const newConfigs = { ...prev, value: "" };

            if (dataResp.hasOwnProperty("allow_free_text")) {
              newConfigs.disableInput = !dataResp.allow_free_text;
            }

            if (
              Array.isArray(dataResp?.option_phrases) &&
              dataResp?.option_phrases?.length > 0
            ) {
              newConfigs.placeholder = dataResp.option_phrases;
            }

            return newConfigs;
          });

          if (dataResp?.messages?.length > 0) {
            setMessages((previous) => [...previous, ...dataResp?.messages]);
          } else throw new Error("no msg found");
        }
      } else {
        const response = await convAiClient.get(
          `/v1/bots/${botId}/conversations/${convId}`,
          {
            signal: abortControllerRef.current.signal,
          }
        );
        if (response?.status !== 200) {
          throw new Error("Network response was not ok");
        }
        const metadata = response?.data?.metadata;
        const dataResp = response?.data?.response;
        if (metadata?.conversation_id) {
          setConvId(metadata?.conversation_id);
          setLocalStorageItem({
            botId: config?.botId,
            filterId: config?.filterId,
            key: "convId",
            value: metadata?.conversation_id || "",
          });
        }

        if (dataResp?.channel === "web") {
          setChatInputConfigs((prev) => {
            const newConfigs = { ...prev, value: "" };
            if (dataResp?.hasOwnProperty("allow_free_text")) {
              newConfigs.disableInput = !dataResp.allow_free_text;
            }

            if (
              Array.isArray(dataResp?.option_phrases) &&
              dataResp?.option_phrases?.length > 0
            ) {
              newConfigs.placeholder = dataResp.option_phrases;
            }

            return newConfigs;
          });
          if (dataResp?.messages?.length > 0) {
            pendingMessagesRef.current = dataResp.messages;
          } else throw new Error("no msg found");
        }
      }
    } catch (error) {
      if (axios.isCancel(error)) return;

      setMessages((previous) => [
        ...previous,
        {
          role: BOT_ROLE,
          turn_id: "1",
          type: "text",
          timestamp: new Date().toISOString(),
          body: {
            text: t("pleaseTryAgain") || BOT_ERROR_MSG,
          },
          id: generateUniqueId(),
        },
      ]);
      setModalConfig(initialModalConfig);
    } finally {
      setBotLoading(false);
    }
  };

  const handleFormSubmit = async (data: any, type?: string) => {
    setBotLoading(true);
    const {
      jobId = "",
      candidateId: candidateIdOrNull = "a7e8c70730bf57b79110c73f2863df69ea",
      applicationId = "",
      templateId = "",
      id = "",
      screensStatus,
    } = data || {};
    const candidateId =
      candidateIdOrNull ?? "a7e8c70730bf57b79110c73f2863df69ea";
    const applySkill = modalConfig?.data?.action_payload?.skill || "";
    const applyState = modalConfig?.data?.action_payload?.state || "";
    setModalConfig(initialModalConfig);
    try {
      const payload = {
        body: null,
        action: {
          type: "text",
          action_type: "action",
          payload: {
            job_id: jobId,
            submission_id: applicationId,
            candidate_id: candidateId,
            form_id: id,
            skill: applySkill,
            screening_result: screensStatus ?? true,
            state: applyState,
            error: type === "formSubmitError" ? true : false,
          },
        },
      };
      await fetchBotResponse(payload, {
        modalConfig: initialModalConfig,
      });
    } catch (error) {
      // console.log("form submit error", error);
    } finally {
      setBotLoading(false);
    }
  };

  const handleInterviewSchedule = async () => {
    setBotLoading(true);
    try {
      const payload = {
        body: null,
        action: {
          type: "text",
          action_type: "action",
          payload: {
            ...schedulerMsg?.data,
            success: true,
          },
        },
      };

      await fetchBotResponse(payload, {
        modalConfig: initialModalConfig,
      });
    } catch (error) {
      console.log("interview submit error", error);
    } finally {
      setBotLoading(false);
    }
  };

  const handleUpdateMessages = (
    messageId: string | string[],
    messages: any
  ) => {
    setOptionSelection(initialOptionSelection);
    setMessages((prevMessages) => {
      const updatedMessages = removeMessageById(prevMessages, messageId);

      const newMessages = [...updatedMessages, ...messages];
      return newMessages;
    });
  };
  const reinitializeChat = async () => {
    handleClearOldData();
    const extractedConfig = config;
    await fetchInitialMsg(extractedConfig?.botId, true);
  };

  const cancelEditMessage = () => {
    setEditState(initialEditState);
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        config,
        loading,
        error,
        showChat,
        hideChat,
        sendData,
        activeView,
        botLoading,
        convId,
        modalConfig,
        schedulerMsg,
        logoList,
        editState,
        showWidgetButton,
        generatePayload,
        genrateUserMsg,
        fetchBotResponse,
        sendMessage,
        fetchConversation,
        createConversation,
        toggleBotInfo,
        handleAddMessage,
        setMessages,
        setModalConfig,
        optionSelection,
        setOptionSelection,
        setSchedulerMsg,
        getTurnId,
        handleFormSubmit,
        reinitializeChat,
        handleUpdateMessages,
        handleInterviewSchedule,
        setConvId,
        chatInputConfigs,
        setChatInputConfigs,
        isPopupOpen,
        setIsPopupOpen,
        initializeData,
        setInitializeData,
        footerModalConfig,
        setFooterModalConfig,
        cancelEditMessage,
        setEditState,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
